/**
 * Auto-level from the room's own lines.
 *
 * A room's HORIZONTALS carry the same tilt as its verticals — they sit at
 * 90°±t. Reading both bands roughly doubles the usable evidence and takes the
 * confident range from about ±2° out to ±3.5°. A histogram peak was tried first
 * and lost to noise spread; the weighted median holds to under 1° where the
 * peak search drifted to zero.
 */
import type { Gray } from './types';

/** Minimum gradient magnitude for an edge pixel to vote. */
const EDGE_FLOOR = 28;

/**
 * @returns the rotation in degrees to apply, or 0 when the evidence is too thin
 *          to act on. Refusing is deliberate: a wrong rotation is worse than
 *          none, and a viewer notices a level horizon far less than a skewed one.
 */
export function estimateTilt(g: Gray): number {
  const { w, h } = g;
  const m0 = Math.round(w * 0.09);
  const m1 = Math.round(h * 0.09);
  const angles: number[] = [];
  const weights: number[] = [];
  let totalWeight = 0;

  for (let y = m1; y < h - m1; y++) {
    for (let x = m0; x < w - m0; x++) {
      const i = y * w + x;
      const gx = g.g[i + 1] - g.g[i - 1];
      const gy = g.g[i + w] - g.g[i - w];
      const mag = Math.hypot(gx, gy);
      if (mag < EDGE_FLOOR) continue;
      totalWeight += mag;

      let ang = (Math.atan2(gy, gx) * 180) / Math.PI;
      ang = (((ang + 90) % 180) + 180) % 180 - 90;

      let residual: number;
      if (Math.abs(ang) <= 12) residual = ang;            // a vertical edge
      else if (ang >= 78) residual = ang - 90;            // a horizontal one
      else if (ang <= -78) residual = ang + 90;
      else continue;                                       // a diagonal: no information

      angles.push(residual);
      weights.push(mag);
    }
  }
  if (angles.length < 200) return 0;

  // Two passes of weighted median: a wide band to find the crowd, a narrow one
  // to place it precisely.
  let median = 0;
  let band = 14;
  for (let pass = 0; pass < 2; pass++) {
    const pairs: Array<[number, number]> = [];
    for (let k = 0; k < angles.length; k++) {
      if (Math.abs(angles[k] - median) <= band) pairs.push([angles[k], weights[k]]);
    }
    if (pairs.length < 80) return 0;
    pairs.sort((a, b) => a[0] - b[0]);
    let sum = 0;
    for (const p of pairs) sum += p[1];
    const half = sum / 2;
    let acc = 0;
    let i = 0;
    for (; i < pairs.length; i++) {
      acc += pairs[i][1];
      if (acc >= half) break;
    }
    median = pairs[Math.min(i, pairs.length - 1)][0];
    band = 4;
  }

  let bandWeight = 0;
  let deviation = 0;
  for (let k = 0; k < angles.length; k++) {
    if (Math.abs(angles[k] - median) > 4) continue;
    bandWeight += weights[k];
    deviation += Math.abs(angles[k] - median) * weights[k];
  }
  if (bandWeight <= 0) return 0;

  const mad = deviation / bandWeight;
  const confidence = bandWeight / totalWeight / Math.max(0.5, mad);
  if (confidence < 0.3 || Math.abs(median) > 8 || Math.abs(median) < 0.25) return 0;
  return -median;
}

/**
 * The scale that a rotation by `deg` needs in order to leave no empty corners.
 * Exposed separately so the composite and the preview crop identically.
 */
export function coverScale(w: number, h: number, deg: number): number {
  const rad = (deg * Math.PI) / 180;
  const ca = Math.abs(Math.cos(rad));
  const sa = Math.abs(Math.sin(rad));
  return Math.max((w * ca + h * sa) / w, (w * sa + h * ca) / h);
}
